import React, {
	Fragment,
	useCallback,
	useEffect,
	useMemo,
	useRef,
	useState,
	type ReactElement,
	type ReactNode,
	type KeyboardEvent,
} from 'react';
import { styled as styled2 } from '@compiled/react';
// eslint-disable-next-line jira/restricted/styled-components-migration, @atlaskit/ui-styling-standard/use-compiled -- Ignored via go/DSP-18766
import styled from 'styled-components';
import noop from 'lodash/noop';
import { ErrorMessage, Field, type FieldProps } from '@atlaskit/form';
import Select from '@atlaskit/select';
import type { AtlaskitSelectRefType } from '@atlaskit/select/types';
import { token } from '@atlaskit/tokens';
import {
	SERVICE_DESK_PROJECT,
	CUSTOMER_SERVICE_PROJECT,
} from '@atlassian/jira-common-constants/src/project-types.tsx';
import { gridSize } from '@atlassian/jira-common-styles/src/main.tsx';
import { JiraIssueCreateErrorBoundary } from '@atlassian/jira-issue-create-analytics-common/src/ui/jira-issue-create-error-boundary/index.tsx';
import { AtlaskitSelectInlineTabActivator } from '@atlassian/jira-inline-config-buttons-for-select/src/AtlaskitSelectInlineTabActivator.tsx';
import { useTriggerIssueCreateModal } from '@atlassian/jira-packages-controllers-use-trigger-issue-create-modal/src/main.tsx';
import { expVal, UNSAFE_noExposureExp } from '@atlassian/jira-feature-experiments';
import { fg } from '@atlassian/jira-feature-gating';
import { getErrorType } from '@atlassian/jira-forge-ui-analytics/src/common/utils/get-error-type/index.tsx';
import { useIntl } from '@atlassian/jira-intl';
import { getProjectSettingsIssueTypesUrl } from '@atlassian/jira-issue-type-config-common/src/getProjectSettingsIssueTypesUrl.tsx';
import {
	ErrorBoundaryBypassInput,
	ErrorBoundaryBypassOutput,
	ErrorBoundaryBypassProvider,
	FieldBusyIndicator,
	UiModificationsErrorHandler,
} from '@atlassian/ui-modifications-core';
import {
	ISSUE_TYPE,
	REQUEST_TYPE,
} from '@atlassian/jira-issue-create-common-types/src/common/constants/graphql-field-types.tsx';
import type {
	IssueType,
	RequestType,
} from '@atlassian/jira-issue-create-common-types/src/common/types/index.tsx';
import { FieldMessage } from '@atlassian/jira-issue-create-commons/src/common/ui/field-message/index.tsx';
import { FieldContainer } from '@atlassian/jira-issue-create-commons/src/common/ui/fields/styled.tsx';
import { useFormContext } from '@atlassian/jira-issue-create-form-context/src/index.tsx';
import { formatOptionLabel } from '@atlassian/jira-issue-field-select-base/src/ui/format-option-label/index.tsx';
import { FormApiProvider } from '@atlassian/jira-issue-form-api/src/controllers/index.tsx';
import { ISSUE_TYPE as ISSUE_TYPE_FIELDTYPE } from '@atlassian/jira-platform-field-config/src/index.tsx';
import { usePrevious } from '@atlassian/jira-platform-react-hooks-use-previous/src/common/utils/index.tsx';
import {
	fireTrackAnalytics,
	fireUIAnalytics,
	useAnalyticsEvents,
} from '@atlassian/jira-product-analytics-bridge';
import { GIC_VIEW_TYPE } from '@atlassian/jira-ui-modifications-view-gic/src/common/constants/index.tsx';
import { FieldDecoratorAsync } from '@atlassian/jira-ui-modifications-view-gic/src/ui/field-decorator/index.tsx';
import { FieldValueDecoratorAsync } from '@atlassian/jira-ui-modifications-view-gic/src/ui/field-value-decorator/index.tsx';
import { CreateIssueTypeModal } from '@atlassian/jira-create-issue-type-modal-wrapper/src/ui/CreateIssueTypeModal.tsx';
import { GIC_VIEW_SOURCE } from '@atlassian/jira-issue-type-config-common/src/types.tsx';
import { CreateIssueTypeUFOExperience } from '@atlassian/jira-issue-create-analytics/src/services/experiences/index.tsx';
import {
	preventEscKeyPropagationKeyPressHandler,
	scrollToElement,
} from '../../../../common/utils/index.tsx';
import { openInNewTabLinkClickEventHandler } from '../../../../common/utils/link-handler/index.tsx';
import { useOpenManageFieldTab } from '../../../../controllers/use-open-manage-field-tab/useOpenManageFieldTab.tsx';
import { MANAGE_WORK_TYPES_ACTION_SUBJECT_ID } from '../../../../common/constants/analytics.tsx';
import {
	ISSUE_TYPE_HELP_TEXT_URL,
	ISSUE_TYPE_PICKER_ELEMENT_ID,
	OPTION_TYPE_MANAGE_ISSUE_TYPE,
	OPTION_TYPE_CREATE_ISSUE_TYPE,
	OPTION_TYPE_EDIT_ISSUE_TYPE,
} from './constants.tsx';
import messages from './messages.tsx';
import {
	useCustomOptionsAndComponentsForInlineIssueTypes,
	useRefocusSelectField,
	getFieldLabel,
	getFirstMatchingRequestType,
	getIssueTypeAndRequestType,
	getRequestAndIssueTypeOptionsGroup,
} from './utils.tsx';
import { WhatsThis } from './request-type-changeboarding/index.tsx';
import { RequestTypeHelpText } from './request-type-help-text/index.tsx';
import { RequestTypeSelect } from './request-type-select/index.tsx';
import type { NoneOption } from './request-type-select/types.tsx';
import type { CreateIssueTypeOption, EditIssueTypeOption, Props, PropsFromUIM } from './types.tsx';

const typePickerMargin = gridSize * 2.25;

const TypePicker = (props: Props) => {
	const { formatMessage } = useIntl();
	const {
		onChange,
		onIssueTypeCreatedOrUpdated,
		issueTypes,
		requestTypes,
		value: selectedRequestOrIssueType,
		isDisabled = false,
		isMini,
		projectType = null,
		projectId,
		projectKey,
		canConfigureIssueTypes = false,
	} = props;

	const { createAnalyticsEvent } = useAnalyticsEvents();

	const previousSelectedRequestOrIssueType = usePrevious(selectedRequestOrIssueType);
	const selectRef = useRef<AtlaskitSelectRefType>(null);

	useEffect(() => {
		// if the default selection has request type of "none"
		// then we will change it to select the first matching request type
		if (
			// we have request type data
			requestTypes &&
			// previously data was loading
			previousSelectedRequestOrIssueType === null &&
			// an issue type is selected
			// i.e. this is the default selection
			// and it has a "none" request type
			selectedRequestOrIssueType &&
			selectedRequestOrIssueType.type === ISSUE_TYPE
		) {
			const firstMatchingRequestType = getFirstMatchingRequestType({
				issueType: selectedRequestOrIssueType,
				allRequestTypes: requestTypes,
			});
			if (firstMatchingRequestType) {
				onChange(firstMatchingRequestType);
				fireTrackAnalytics(createAnalyticsEvent({}), 'gicRequestTypeSelect forceDefaultNotNone');
			}
		}
	}, [
		createAnalyticsEvent,
		onChange,
		previousSelectedRequestOrIssueType,
		requestTypes,
		selectedRequestOrIssueType,
	]);

	const getIssueTypeHelpText = useCallback(
		() => (
			<FieldMessage
				fieldHelpTextUrl={ISSUE_TYPE_HELP_TEXT_URL}
				fieldName="Issue Type"
				onLinkClickEvent={openInNewTabLinkClickEventHandler}
				isMini={isMini}
			/>
		),
		[isMini],
	);

	// eslint-disable-next-line jira/ff/unsafe-no-exposure
	const [inlineIssueTypeConfigExp, fireExposure] = UNSAFE_noExposureExp(
		'jira_create_issue_type_in_gic_view',
	);

	// eslint-disable-next-line jira/ff/unsafe-no-exposure
	const [inlineCombinedImpactConfigExp, fireCombinedImpactExposure] = UNSAFE_noExposureExp(
		'combined_adoption_configuration_experiment',
	);

	// TODO: Remove below when cleaning up jira_gic_inline_create_work_type_refactor
	const editIssueTypeButtonRef = useRef<HTMLElement>(null);
	const createIssueTypeModalRef = useRef<{ load: () => void; preload: () => void }>(null);
	const onPreloadCreateIssueTypeModal = () => {
		createIssueTypeModalRef.current?.preload();
	};
	const onLoadCreateIssueTypeModal = () => {
		createIssueTypeModalRef.current?.load();
	};

	const [
		{
			meta: { submitFieldwiseErrors },
		},
	] = useFormContext();

	useEffect(() => {
		if (submitFieldwiseErrors?.issuetype) {
			scrollToElement(ISSUE_TYPE_PICKER_ELEMENT_ID);
		}
	}, [submitFieldwiseErrors?.issuetype]);

	const isInlineIssueTypeConfigEnabled = inlineIssueTypeConfigExp.get(
		'isInlineIssueTypeConfigEnabled',
		false,
	);

	const isInlineCombinedImpactConfigEnabled = inlineCombinedImpactConfigExp.get('isEnabled', false);

	const refocusSelectField =
		isInlineIssueTypeConfigEnabled || isInlineCombinedImpactConfigEnabled
			? // eslint-disable-next-line react-hooks/rules-of-hooks
				useRefocusSelectField(selectRef)
			: noop;

	const openManageWorkTypesTab =
		isInlineIssueTypeConfigEnabled || isInlineCombinedImpactConfigEnabled
			? // eslint-disable-next-line react-hooks/rules-of-hooks
				useOpenManageFieldTab({
					source: MANAGE_WORK_TYPES_ACTION_SUBJECT_ID,
					url: getProjectSettingsIssueTypesUrl({ projectKey, projectType: projectType ?? '' }),
				})
			: noop;

	const [, { closeIssueCreateModal }] = useTriggerIssueCreateModal();

	const fields = useMemo(() => {
		let fieldsNode: ReactElement;

		const projectTypeCheck = fg('jcs_master_flag')
			? projectType === SERVICE_DESK_PROJECT || projectType === CUSTOMER_SERVICE_PROJECT
			: projectType === SERVICE_DESK_PROJECT;
		if (projectTypeCheck && requestTypes !== undefined) {
			// get the options for the 2 select fields
			const { issueTypeOptions, requestTypeOptions } = getRequestAndIssueTypeOptionsGroup(
				issueTypes,
				requestTypes,
				formatMessage,
			);

			// extract the selected option for the 2 select fields from the aggregated value
			const { issueType: selectedIssueType, requestType: selectedRequestType } =
				getIssueTypeAndRequestType({
					allIssueTypes: issueTypeOptions,
					requestOrIssueType: selectedRequestOrIssueType,
					formatMessage,
				});

			// fire UI events when the selects are opened
			const onIssueTypeSelectOpen = () =>
				fireUIAnalytics(createAnalyticsEvent({}), 'gicIssueTypeSelect open');
			const onRequestTypeSelectOpen = () =>
				fireUIAnalytics(createAnalyticsEvent({}), 'gicRequestTypeSelect open');

			// on issue type selection we want to pre-select a matching request type
			const onIssueTypeChange = (issueType: IssueType) => {
				if (issueType.value === selectedIssueType?.value) {
					return;
				}
				const firstMatchingRequestType = getFirstMatchingRequestType({
					issueType,
					allRequestTypes: requestTypes,
				});
				onChange(firstMatchingRequestType || issueType);
				fireUIAnalytics(createAnalyticsEvent({}), 'gicIssueTypeSelect selected');
			};

			// on request type selection we want to select the chosen request type,
			// or if 'none' is selected, we select the issue type
			const onRequestTypeChange = (requestTypeOrNone: RequestType | NoneOption) => {
				if (requestTypeOrNone.value === selectedRequestType?.value) {
					return;
				}
				let isNoneSelected = true;
				if (requestTypeOrNone.type === REQUEST_TYPE) {
					onChange(requestTypeOrNone);
					isNoneSelected = false;
				} else if (selectedIssueType !== null) {
					onChange(selectedIssueType);
				}
				fireUIAnalytics(createAnalyticsEvent({}), 'gicRequestTypeSelect selected', {
					isNoneSelected,
				});
			};

			const requestTypeHelpText =
				selectedRequestType && selectedRequestType.type === REQUEST_TYPE
					? selectedRequestType.helpText
					: '';
			fieldsNode = (
				<>
					<FieldContainer width={isMini ? '100%' : undefined} marginTop={typePickerMargin}>
						<Field
							name="issue-type-picker"
							label={formatMessage(
								expVal('issue-terminology-refresh-m2-replace', 'isEnabled', false)
									? messages.issueTypeFieldLabelIssueTermRefresh
									: messages.issueTypeFieldLabel,
							)}
							isRequired
						>
							{({ fieldProps: { id, isRequired } }) => (
								<>
									<Select
										inputId={id}
										required={isRequired}
										options={issueTypeOptions}
										value={selectedIssueType}
										// @ts-expect-error - TS2322 - Type '(issueType: IssueType) => void' is not assignable to type '(value: Option | null, action: ActionMeta<Option>) => void'.
										onChange={onIssueTypeChange}
										onMenuOpen={onIssueTypeSelectOpen}
										isClearable={false}
										formatOptionLabel={formatOptionLabel}
										placeholder=""
										id={ISSUE_TYPE_PICKER_ELEMENT_ID}
										isDisabled={isDisabled}
										onKeyDown={preventEscKeyPropagationKeyPressHandler}
									/>
									{getIssueTypeHelpText()}
									{submitFieldwiseErrors?.issuetype && (
										<ErrorMessage>{submitFieldwiseErrors.issuetype}</ErrorMessage>
									)}
								</>
							)}
						</Field>
					</FieldContainer>
					<FieldContainer width="100%" marginTop={typePickerMargin}>
						<RequestTypeFieldWrapper>
							<Field
								name="request-type-picker"
								label={
									<RequestTypeFieldLabelWrapper>
										<div>
											{formatMessage(messages.requestTypeFieldLabel)}
											<RequiredIndicator>*</RequiredIndicator>
										</div>
										<WhatsThis />
									</RequestTypeFieldLabelWrapper>
								}
							>
								{({ fieldProps: { id } }) => (
									<RequestTypeSelect
										inputId={id}
										options={requestTypeOptions}
										value={selectedRequestType}
										onChange={onRequestTypeChange}
										onMenuOpen={onRequestTypeSelectOpen}
										// id for cypress testing
										id="issue-create.ui.modal.create-form.type-picker.request-type-select"
									/>
								)}
							</Field>
						</RequestTypeFieldWrapper>
						{requestTypeHelpText && <RequestTypeHelpText helpText={requestTypeHelpText} />}
					</FieldContainer>
				</>
			);
		} else {
			// there is no request type data, just show issue type select
			const label = getFieldLabel(Boolean(requestTypes?.length), formatMessage);
			const onIssueTypeChange = (
				selectedIssueType:
					| IssueType
					| CreateIssueTypeOption
					| EditIssueTypeOption
					| RequestType
					| null,
			): void => {
				if (!selectedIssueType) return;
				// Button Manage issue type
				if (
					(isInlineIssueTypeConfigEnabled || isInlineCombinedImpactConfigEnabled) &&
					selectedIssueType.value === OPTION_TYPE_MANAGE_ISSUE_TYPE
				) {
					openManageWorkTypesTab();
					return;
				}
				// Button Create issue type
				const isCreateIssueTypeAction = selectedIssueType.type === OPTION_TYPE_CREATE_ISSUE_TYPE;
				if (
					createIssueTypeModalRef.current &&
					isCreateIssueTypeAction &&
					!fg('jira_gic_inline_create_work_type_refactor')
				) {
					const analyticsEvent = createAnalyticsEvent({
						action: 'clicked',
						actionSubject: 'button',
					});
					fireUIAnalytics(analyticsEvent, 'createIssueTypeButton');
					createIssueTypeModalRef.current.load();
					return;
				}
				if (isCreateIssueTypeAction && fg('jira_gic_inline_create_work_type_refactor')) {
					const analyticsEvent = createAnalyticsEvent({
						action: 'clicked',
						actionSubject: 'button',
					});
					fireUIAnalytics(analyticsEvent, 'createIssueTypeButton');
					return;
				}

				// Button Edit issue type
				const isEditIssueTypeAction = selectedIssueType.type === OPTION_TYPE_EDIT_ISSUE_TYPE;
				if (
					editIssueTypeButtonRef.current &&
					isEditIssueTypeAction &&
					!fg('jira_gic_inline_create_work_type_refactor')
				) {
					// Analytics event is triggered in the EditIssueTypeModalTrigger
					editIssueTypeButtonRef.current.click();
					return;
				}
				if (isEditIssueTypeAction && fg('jira_gic_inline_create_work_type_refactor')) {
					const analyticsEvent = createAnalyticsEvent({
						action: 'clicked',
						actionSubject: 'button',
					});
					fireUIAnalytics(analyticsEvent, 'editIssueTypeButton');
					return;
				}

				if (
					selectedIssueType.value === selectedRequestOrIssueType?.value ||
					OPTION_TYPE_CREATE_ISSUE_TYPE === selectedIssueType.type ||
					OPTION_TYPE_EDIT_ISSUE_TYPE === selectedIssueType.type
				) {
					return;
				}

				const analyticsEvent = createAnalyticsEvent({
					action: 'selected',
					actionSubject: 'option',
				});
				fireUIAnalytics(analyticsEvent, 'issueType');
				onChange(selectedIssueType);
			};

			const Component = ({ areUiModificationsBusy, allowedValues, isReadOnly }: PropsFromUIM) => {
				const { options, components, noOptionWrapperRef } =
					isInlineIssueTypeConfigEnabled || isInlineCombinedImpactConfigEnabled
						? // eslint-disable-next-line react-hooks/rules-of-hooks
							useCustomOptionsAndComponentsForInlineIssueTypes({
								options: allowedValues ?? [],
								canConfigureIssueTypes,
								projectId,
								projectKey,
								issueTypeId: selectedRequestOrIssueType?.value ?? '',
								onPreloadCreateIssueTypeModal,
								onLoadCreateIssueTypeModal,
								onIssueTypeCreatedOrUpdated,
								onCreateModalClose: refocusSelectField,
								onEditModalClose: refocusSelectField,
								editIssueTypeButtonRef,
							})
						: {
								options: allowedValues,
								components: undefined,
								noOptionWrapperRef: undefined,
							};

				const isFixingA11yForInlineButtons =
					(isInlineIssueTypeConfigEnabled || isInlineCombinedImpactConfigEnabled) &&
					fg('inline_config_a11y_improvements');

				const SelectWrapper = isFixingA11yForInlineButtons
					? AtlaskitSelectInlineTabActivator
					: Fragment;

				const [inputValue, setInputValue] = useState('');
				const [isMenuOpen, setIsMenuOpen] = useState(false);

				const handleIssueTypesDropdownOpen = () => {
					fireUIAnalytics(createAnalyticsEvent({}), 'gicIssueTypeSelect open');
					if (canConfigureIssueTypes) {
						fireExposure();
						// NOTE: same FG in front of the following experiments, new activated tenants pass 100% for combined_adoption_configuration_experiment and fail 100% on the other...
						fireCombinedImpactExposure();
						expVal('combined_adoption_configuration_experiment_aa', 'isEnabled', false);
					}
				};

				// eslint-disable-next-line jira/jira-ssr/no-unchecked-globals-usage
				const fieldId = document.activeElement?.id;

				useEffect(() => {
					if (fieldId?.includes('type-picker') && fg('fix-gryf-a11y-issues')) {
						selectRef?.current?.focus();
					}
				}, [fieldId]);
				const attributes = useMemo(
					() => ({
						projectId,
						isMenuOpen,
						fieldId,
					}),
					[fieldId, isMenuOpen],
				);

				return (
					<JiraIssueCreateErrorBoundary
						id="withFormField"
						packageName="jiraIssueCreateModal"
						sendToPrivacyUnsafeSplunk
						{...(fg('jira-issue-create-error-boundary-enabled') ? { attributes } : {})}
					>
						<Field
							name="type-picker"
							label={label}
							isRequired
							elementAfterLabel={
								areUiModificationsBusy ? (
									<FieldBusyIndicator fieldId={ISSUE_TYPE_FIELDTYPE} />
								) : undefined
							}
						>
							{/* eslint-disable-next-line @typescript-eslint/no-explicit-any */}
							{({ fieldProps }: { fieldProps: FieldProps<any, HTMLInputElement> }) => {
								const renderFieldContent = ({
									id,
									isRequired, // eslint-disable-next-line @typescript-eslint/no-explicit-any
								}: FieldProps<any, HTMLInputElement>) => (
									<>
										<SelectWrapper
											setInputValue={setInputValue}
											setIsMenuOpen={setIsMenuOpen}
											inputValue={inputValue}
											isMenuOpen={isMenuOpen}
										>
											<Select
												ref={selectRef}
												inputId={id}
												menuIsOpen={isFixingA11yForInlineButtons ? isMenuOpen : undefined}
												inputValue={isFixingA11yForInlineButtons ? inputValue : undefined}
												required={isRequired}
												options={options}
												components={components}
												// @ts-expect-error - TS2322 - Type '(selectedIssueType: IssueType) => void' is not assignable to type '(value: Option | null, action: ActionMeta<Option>) => void'.
												onChange={onIssueTypeChange}
												onMenuOpen={handleIssueTypesDropdownOpen}
												value={selectedRequestOrIssueType}
												isClearable={false}
												formatOptionLabel={formatOptionLabel}
												placeholder=""
												id={ISSUE_TYPE_PICKER_ELEMENT_ID}
												isDisabled={!!(isDisabled || isReadOnly)}
												onKeyDown={(e: KeyboardEvent<HTMLElement>) => {
													preventEscKeyPropagationKeyPressHandler(e);
												}}
												onInputChange={(value, { action }) => {
													if (
														isFixingA11yForInlineButtons &&
														// This is to prevent the search value to be cleared when tabbing to the Create Issue Type button in Empty no results state
														((action !== 'input-blur' && action !== 'menu-close') ||
															!noOptionWrapperRef?.current)
													) {
														setInputValue(value);
													}
												}}
											/>
										</SelectWrapper>
										{getIssueTypeHelpText()}
										{submitFieldwiseErrors?.issuetype && (
											<ErrorMessage>{submitFieldwiseErrors.issuetype}</ErrorMessage>
										)}
									</>
								);

								return (
									<ErrorBoundaryBypassProvider>
										<ErrorBoundaryBypassOutput />
										<JiraIssueCreateErrorBoundary
											id="issueAdjustmentsFieldValueDecorator"
											packageName="jiraIssueCreateModal"
											fallback={() => (
												<>
													<UiModificationsErrorHandler />
													{renderFieldContent(fieldProps)}
												</>
											)}
											sendToPrivacyUnsafeSplunk
											attributes={(error: Error) => ({
												errorType: getErrorType(error),
												viewType: GIC_VIEW_TYPE,
												fieldId: ISSUE_TYPE_FIELDTYPE,
												fieldType: ISSUE_TYPE_FIELDTYPE,
											})}
										>
											<FieldValueDecoratorAsync
												fieldId={ISSUE_TYPE_FIELDTYPE}
												fieldType={ISSUE_TYPE_FIELDTYPE}
												fieldProps={{
													...fieldProps,
													value: selectedRequestOrIssueType,
												}}
											>
												{(interceptedFieldProps) => (
													<ErrorBoundaryBypassInput>
														{renderFieldContent(
															// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
															interceptedFieldProps as FieldProps<
																// eslint-disable-next-line @typescript-eslint/no-explicit-any
																any,
																HTMLInputElement
															>,
														)}
													</ErrorBoundaryBypassInput>
												)}
											</FieldValueDecoratorAsync>
										</JiraIssueCreateErrorBoundary>
									</ErrorBoundaryBypassProvider>
								);
							}}
						</Field>
						{(isInlineIssueTypeConfigEnabled || isInlineCombinedImpactConfigEnabled) &&
							projectKey &&
							!fg('jira_gic_inline_create_work_type_refactor') && (
								<CreateIssueTypeModal
									onIssueTypeCreated={onIssueTypeCreatedOrUpdated}
									onOpenManageTypes={closeIssueCreateModal}
									ufoExperience={CreateIssueTypeUFOExperience}
									onOpen={noop}
									onClose={refocusSelectField}
									ref={createIssueTypeModalRef}
									source={GIC_VIEW_SOURCE}
									projectKey={projectKey}
									shouldFetchProjectData
								/>
							)}
					</JiraIssueCreateErrorBoundary>
				);
			};

			const typePickerComponentProps = {
				fieldId: ISSUE_TYPE_FIELDTYPE,
				fieldName: label,
				fieldType: ISSUE_TYPE_FIELDTYPE,
				isRequired: true,
				gqlType: '',
				allowedValues: issueTypes,
			};

			const setFieldValue = (_: string, value: unknown) =>
				// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
				onIssueTypeChange(value as IssueType);
			const getFormState = () => ({
				values: {
					[typePickerComponentProps.fieldId]: selectedRequestOrIssueType,
				},
			});

			fieldsNode = (
				<FormApiProvider setFieldValue={setFieldValue} getFormState={getFormState}>
					<FieldContainer width={isMini ? '100%' : undefined} marginTop={typePickerMargin}>
						<JiraIssueCreateErrorBoundary
							id="globalIssueCreateIssueAdjustmentsAdapter"
							packageName="issueCreateModal"
							fallback={() => (
								<>
									<UiModificationsErrorHandler />
									<Component {...typePickerComponentProps} />
								</>
							)}
							sendToPrivacyUnsafeSplunk
							attributes={(error: Error) => ({
								errorType: getErrorType(error),
								viewType: GIC_VIEW_TYPE,
								fieldId: typePickerComponentProps.fieldId,
								fieldType: typePickerComponentProps.fieldType,
							})}
						>
							<FieldDecoratorAsync
								Component={Component}
								componentProps={typePickerComponentProps}
							/>
						</JiraIssueCreateErrorBoundary>
					</FieldContainer>
				</FormApiProvider>
			);
		}

		return fieldsNode;
	}, [
		projectType,
		requestTypes,
		issueTypes,
		formatMessage,
		selectedRequestOrIssueType,
		isMini,
		createAnalyticsEvent,
		onChange,
		isDisabled,
		getIssueTypeHelpText,
		submitFieldwiseErrors?.issuetype,
		isInlineIssueTypeConfigEnabled,
		isInlineCombinedImpactConfigEnabled,
		openManageWorkTypesTab,
		canConfigureIssueTypes,
		projectId,
		projectKey,
		onIssueTypeCreatedOrUpdated,
		refocusSelectField,
		closeIssueCreateModal,
		fireExposure,
		fireCombinedImpactExposure,
	]);

	return fields;
};

export default TypePicker;

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const RequestTypeFieldWrapper = styled.div<{
	children?: ReactNode;
}>({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	'> div': {
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
		'> label': {
			width: '100%',
		},
	},
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const RequiredIndicator = styled2.span<{
	children?: ReactNode;
}>({
	color: token('color.text.danger'),
	paddingLeft: token('space.025'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const RequestTypeFieldLabelWrapper = styled2.div<{ children?: ReactNode }>({
	display: 'flex',
	flexDirection: 'row',
	alignItems: 'baseline',
	justifyContent: 'space-between',
	width: '100%',
	height: '100%',
	marginTop: token('space.075'),
});
